import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { ShipmentDetailDrawer } from "../components/shipment/ShipmentDetailDrawer";
import { ShippableOrderDetailDrawer } from "../components/shipment/ShippableOrderDetailDrawer";
import { Drawer } from "../components/Drawer";
import { Icon } from "../components/ui/Icon";
import { ToggleSwitch } from "../components/ui/ToggleSwitch";
import { SearchSelect } from "../components/ui/SearchSelect";
import { TablePager, MobilePager } from "../components/ui/TablePager";
import { PageTabs } from "../components/ui/PageTabs";
import { HardDeleteConfirm } from "../components/ui/HardDeleteConfirm";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { PromptDialog } from "../components/ui/PromptDialog";
import { ToastStack, type ToastItem } from "../components/ui/Toast";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard, KV } from "../components/mobile/primitives";
import "./Items.css";

interface ShipmentRow {
  id: string;
  code: string;
  orderCode: string;
  customerName: string;
  status: "draft" | "confirmed" | "cancelled";
  shippedAt: string | null;
  lineCount: number;
  freightParty: string | null;
  freightAmount: number | null;
  freightCurrency: string | null;
  /** Araç (çekici) + varsa dorse plakası — hızlı aramada da eşleşir. */
  vehiclePlate: string | null;
  trailerPlate: string | null;
  /** Planlanan sevk tarihi; planın YAPILDIĞI tarih `createdAt`. */
  plannedDate: string | null;
  createdAt: string | null;
  /** Ürün özeti: sevk edilecek ve siparişe esas miktar (ürün+birim bazında toplanmış). */
  kalemler: Array<{ code: string; name: string; sevk: number; siparis: number; uom: string }>;
  /**
   * İçi BOŞ plan (lot satırı, şoför/plaka/evrak… yok) — sunucunun silme ucuyla AYNI koşulundan
   * (BOS_PLAN_KOSULU) gelir; burada yeniden türetilmez. true → çöp kutusu onay sormadan siler.
   */
  bosPlan: boolean;
}

const STATUS_BADGE: Record<ShipmentRow["status"], { cls: string; label: string }> = {
  draft: { cls: "b-warn", label: "Hazırlanıyor" },
  confirmed: { cls: "b-success", label: "Sevk Edildi" },
  cancelled: { cls: "", label: "İptal" },
};

function fmtDate(d: string | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}
type ShipTab = "ready" | "shipments" | "done";
const SHIP_TABS = [
  { key: "ready" as const, label: "Sevkiyata Hazır" },
  { key: "shipments" as const, label: "Sevkiyatlar" },
  { key: "done" as const, label: "Tamamlanan" },
];

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}

// Sevk edilebilir sipariş seçim drawer'ı
function CreateShipmentDrawer({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string, warnings?: string[]) => void }) {
  const qc = useQueryClient();
  const shippableQ = trpc.shipment.shippableOrders.useQuery({ pageSize: 200 }, { enabled: open });
  const createMut = trpc.shipment.createFromOrder.useMutation();
  const [err, setErr] = useState<string | null>(null);

  async function pick(orderId: string) {
    setErr(null);
    try {
      const res = await createMut.mutateAsync({ orderId });
      await qc.invalidateQueries();
      onClose();
      onCreated(res.shipmentId, res.warnings);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const orders = shippableQ.data?.rows ?? [];

  return (
    <Drawer open={open} onClose={onClose} title="Yeni Sevkiyat" subtitle="Sevk edilecek onaylı siparişi seçin (rezerve stok)">
      {err ? <div className="form-banner-err">{err}</div> : null}
      {shippableQ.isLoading ? (
        <div className="d-empty"><span className="m">Yükleniyor…</span></div>
      ) : orders.length === 0 ? (
        <div className="d-empty">
          <Icon name="package" size={28} />
          <span className="m">Sevk edilebilir sipariş yok.<br />Önce sipariş oluşturup onaylayın (rezervasyon).</span>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {orders.map((o) => (
            <button key={o.id} type="button" onClick={() => pick(o.id)} disabled={createMut.isPending}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", background: "var(--white)", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
              <span>
                <span className="mono" style={{ fontWeight: 600, fontSize: 13 }}>{o.code}</span>
                <span style={{ display: "block", fontSize: 12, color: "var(--neutral-500)", marginTop: 2 }}>{o.customerName}</span>
              </span>
              <span style={{ fontSize: 12, color: "var(--info)", fontWeight: 500 }}>Sevkiyat oluştur →</span>
            </button>
          ))}
        </div>
      )}
    </Drawer>
  );
}

export function ShipmentsPage() {
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null); // hazır sipariş salt-okunur detayı
  const [showCancelled, setShowCancelled] = useState(false); // varsayılan: iptaller gizli
  /** Çok seçimli süzgeçler — boş dizi = "tümü" (uç `.min(1)` istediği için undefined gönderilir). */
  const [customerIds, setCustomerIds] = useState<string[]>([]);
  const [itemIds, setItemIds] = useState<string[]>([]);
  /** `not`: onaysız silme sunucuda reddedilip pencereye düşüldüyse NEDENİ (ör. "plan artık boş değil"). */
  const [delTarget, setDelTarget] = useState<{ id: string; code: string; not?: string } | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = useCallback((t: Omit<ToastItem, "id">) => {
    const id = String(++toastSeq.current);
    setToasts((prev) => [...prev.slice(-4), { ...t, id }]); // aynı anda en çok 5 toast görünür
  }, []);
  const dismissToast = useCallback((id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);
  /**
   * SEVK EDİLMİŞ sevkiyatı geri al + sil (yalnız süper admin).
   * İki adım: (1) ne olacağının açık listesi, (2) gerekçe — gerekçe denetim kaydına yazılır.
   * `revStep` adım, `revResult` başarı özeti (kaç lot yeniden planlanabilir oldu).
   */
  const [revTarget, setRevTarget] = useState<{ id: string; code: string; orderCode: string | null; lineCount: number } | null>(null);
  const [revStep, setRevStep] = useState<"warn" | "reason">("warn");
  const [revResult, setRevResult] = useState<string | null>(null);
  const [readySearch, setReadySearch] = useState(""); // "Sevkiyata Hazır Siparişler" — SUNUCU TARAFI arama
  const [readyPage, setReadyPage] = useState(1);
  const [tab, setTab] = useState<ShipTab>("ready");
  const changeTab = (t: ShipTab) => { setTab(t); setPage(1); }; // sekme değişince liste sayfası başa

  /**
   * Süzgeç kaynakları — YALNIZ liste sekmelerinde çekilir ("Sevkiyata Hazır"da gereksiz).
   * `staleTime` yüksek: ana veri, sayfa gezinirken tekrar tekrar sorgulanmasın.
   */
  const musteriQ = trpc.customer.list.useQuery({ page: 1, pageSize: 200 }, { enabled: tab !== "ready", staleTime: 5 * 60_000 });
  const mamulQ = trpc.finishedProduct.list.useQuery({ page: 1, pageSize: 200 }, { enabled: tab !== "ready", staleTime: 5 * 60_000 });
  const musteriSecenek = useMemo(
    () => (musteriQ.data?.rows ?? []).map((c) => ({ value: c.id, label: c.name })).sort((a, b) => a.label.localeCompare(b.label, "tr")),
    [musteriQ.data],
  );
  const urunSecenek = useMemo(
    () => (mamulQ.data?.rows ?? []).map((r) => ({ value: r.id, label: r.name, desc: r.code })).sort((a, b) => a.label.localeCompare(b.label, "tr")),
    [mamulQ.data],
  );

  const qc = useQueryClient();
  // "done" sekmesi = yalnız TAMAMLANAN (onaylı/sevk edilmiş) sevkiyatlar — aynı liste ucu, status filtresiyle.
  // "Sevkiyatlar" sekmesi = hazırlanan (taslak) + istenirse iptaller; TAMAMLANANLAR HARİÇ (excludeConfirmed)
  // — sevk edilen sevkiyat iki listede birden görünmez, yalnız "Tamamlanan"da olur.
  // Müşteri/ürün süzgeçleri İKİ sekmede de görünür → İKİ sekmede de gönderilir. (Eskiden "Tamamlanan" girdisi
  // bunları düşürüyordu: seçim ekranda duruyor ama liste süzülmüyordu — sessiz yok sayma.)
  const input = useMemo(() => {
    const suzgec = {
      customerIds: customerIds.length ? customerIds : undefined,
      itemIds: itemIds.length ? itemIds : undefined,
    };
    return tab === "done"
      ? { page, pageSize, q: q.trim() || undefined, status: "confirmed" as const, ...suzgec }
      : { page, pageSize, q: q.trim() || undefined, includeCancelled: showCancelled, excludeConfirmed: true, ...suzgec };
  }, [tab, page, q, showCancelled, customerIds, itemIds]);
  /** Boş liste nedeni — arama/süzgeç varken genel "sevkiyat yok" metni yanıltır; neyin süzdüğünü söyle. */
  const suzgecAktif = customerIds.length > 0 || itemIds.length > 0;
  const bosNeden = [
    q.trim() && `"${q.trim()}" araması`,
    customerIds.length && `${customerIds.length} müşteri`,
    itemIds.length && `${itemIds.length} ürün`,
  ].filter(Boolean).join(" + ");
  const bosMetin = bosNeden ? `${bosNeden} ile eşleşen ${tab === "done" ? "tamamlanan" : "hazırlanan"} sevkiyat yok.` : null;
  // placeholderData: AYNI sekme içinde sayfa/arama değişiminde önceki veri korunur (boş-flaş olmaz). SEKME
  // değişiminde taşınMAZ: yoksa taslak satırlar bir an "Tamamlanan" başlığı altında (sil butonuyla) görünür ve
  // bayat totalPages ile olmayan sayfaya geçilebilir. Sekme kimliği = önceki sorgunun status alanı var mı.
  const shipsQ = trpc.shipment.list.useQuery(input, {
    enabled: tab !== "ready", // liste yalnız "Sevkiyatlar"/"Tamamlanan" sekmelerinde gösterilir
    placeholderData: (prev, prevQuery) => {
      const prevInput = (prevQuery?.queryKey as unknown as [unknown, { input?: { status?: string } }] | undefined)?.[1]?.input;
      const sameTab = !!prevInput && (prevInput.status !== undefined) === (tab === "done");
      return sameTab ? prev : undefined;
    },
  });
  // readyQ yalnız "Sevkiyata Hazır" sekmesinde çalışır (diğer sekmelerde gereksiz tam tarama + groupBy yapmasın).
  const readyQ = trpc.shipment.shippableOrders.useQuery(
    { page: readyPage, pageSize: 10, q: readySearch.trim() || undefined },
    { enabled: tab === "ready", placeholderData: (prev) => prev },
  );
  const createMut = trpc.shipment.createFromOrder.useMutation();
  const createManualMut = trpc.shipment.createManual.useMutation();
  const removeMut = trpc.shipment.remove.useMutation();
  // BOŞ planın onaysız silinmesi detay çekmecesiyle AYNI uçtan (`cancel` + yalnizBossa, yetki ship:order):
  // aynı iş iki ekranda farklı yetki istemesin. Dolu planın kodlu silmesi `remove` (süper admin) AYNEN.
  const bosSilMut = trpc.shipment.cancel.useMutation();
  const reverseMut = trpc.shipment.reverseAndDelete.useMutation();
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const isSuper = !!meQ.data?.user?.isSuperAdmin; // geri alma stok defterini geri sarar → yalnız süper admin
  const [planErr, setPlanErr] = useState<string | null>(null);
  const [planNote, setPlanNote] = useState<string | null>(null);
  const [planChoice, setPlanChoice] = useState<{ orderId: string; draftCode: string } | null>(null); // açık taslak varken "mevcut/yeni" seçimi

  /**
   * İDARİ KAPATMA — stok düşer, cari borç yazar, sipariş sevk edilir. Yalnız süper admin.
   * Evrak alanları BOŞ kalır (bkz. routers/shipment.ts adminClose): uydurma plaka/irsaliye
   * yazmak yerine sevkiyat "idari kapatma" olarak işaretlenir.
   */
  const adminCloseMut = trpc.shipment.adminClose.useMutation();
  const [closeTarget, setCloseTarget] = useState<{ orderId: string; code: string; customer: string } | null>(null);
  const [closeResult, setCloseResult] = useState<string | null>(null);
  async function adminCloseOrder(reason: string) {
    if (!closeTarget) return;
    try {
      const r = await adminCloseMut.mutateAsync({ orderId: closeTarget.orderId, reason });
      await qc.invalidateQueries();
      setCloseTarget(null);
      setCloseResult(
        `${closeTarget.code} kapatıldı → ${r.shipmentCode} · ${r.lineCount} satır · sevk tarihi ${fmtDate(r.plannedDate)}` +
        (r.orderShipped ? " · sipariş SEVK EDİLDİ" : " · sipariş kısmen açık kaldı"),
      );
    } catch { /* hata adminCloseMut.error ile dialog içinde görünür; dialog açık kalır */ }
  }
  const readyOrders = readyQ.data?.rows ?? [];
  const readyTotal = readyQ.data?.total ?? 0;
  const readyTotalPages = Math.max(1, Math.ceil(readyTotal / (readyQ.data?.pageSize ?? 10))); // bölen sunucudan (hardcoded 10 ile senkron kalır)
  // Toplam azalırsa (sipariş sevk edilince) mevcut sayfa aralık dışı kalmasın — YALNIZ gerçek veri varken
  // (yükleme sırasında data yoksa clamp etme, yoksa sayfa değişimini geri alır).
  useEffect(() => { if (readyQ.data && readyPage > readyTotalPages) setReadyPage(readyTotalPages); }, [readyQ.data, readyTotalPages, readyPage]);

  async function removeShipment() {
    if (!delTarget) return;
    try {
      await removeMut.mutateAsync({ id: delTarget.id });
      await qc.invalidateQueries();
      pushToast({ tone: "success", message: `${delTarget.code} sevkiyat planı silindi.` });
      setDelTarget(null);
    } catch { /* hata removeMut.error ile HardDeleteConfirm banner'ında gösterilir; dialog açık kalır */ }
  }

  /**
   * ÇÖP KUTUSU — BOŞ plan pencere AÇMADAN silinir; dolu planda "kodu yazın" onayı AYNEN.
   * "Boş mu?" kararı burada TÜRETİLMEZ: `bosPlan` bayrağı da `yalnizBossa` silmesi de sunucudaki
   * tek koşuldan gelir ve silme kararı sunucuda DELETE'in kendi WHERE'inde verilir. Bayrak bayatsa
   * (arada lot/evrak eklendi) sunucu PRECONDITION_FAILED döner → normal onay penceresi sunucunun
   * gerekçesiyle açılır. Diğer hatalar toast'la söylenir — sessiz başarısızlık yok.
   */
  const bosSilRef = useRef(false); // çift tık iki istek atmasın: isPending ancak bir sonraki render'da görünür
  // Görünür kilit bekçiyle AYNI süre açık kalır: `isPending` mutasyon biter bitmez düşer ama bekçi
  // liste tazelenene (`invalidateQueries`) kadar tutulur — arada düğme etkin GÖRÜNÜP tıklamayı
  // sessizce yutmasın (dolu planın kod penceresi de o sürede açılmazdı).
  const [bosSiliniyor, setBosSiliniyor] = useState(false);
  async function silTiklandi(s: ShipmentRow) {
    // Bekçi `reset()`ten ÖNCE: uçuştaki silmede `reset()` gözlemciyi mutasyondan koparır →
    // isPending false'a döner ve düğmeler istek sürerken yeniden tıklanabilir olur (ölçüldü).
    if (bosSilRef.current) return;
    removeMut.reset();
    if (!s.bosPlan) { setDelTarget({ id: s.id, code: s.code }); return; }
    bosSilRef.current = true;
    setBosSiliniyor(true);
    try {
      await bosSilMut.mutateAsync({ id: s.id, yalnizBossa: true });
      await qc.invalidateQueries();
      pushToast({ tone: "success", message: `${s.code} boş plan silindi.` });
    } catch (e) {
      const err = e as { data?: { code?: string } | null; message: string };
      await qc.invalidateQueries(); // bayrak/liste sunucudaki gerçeğe dönsün
      if (err.data?.code === "PRECONDITION_FAILED") {
        // Pencerenin kod adımında "hata" gibi görünmesin: neden 1. adımda NOT olarak gösterilir.
        removeMut.reset();
        setDelTarget({ id: s.id, code: s.code, not: err.message });
      } else {
        pushToast({ tone: "danger", title: `${s.code} silinemedi`, message: err.message });
      }
    } finally {
      bosSilRef.current = false;
      setBosSiliniyor(false);
    }
  }

  /** Sevk edilmiş sevkiyatı geri al + sil. Başarıda ne olduğunu ÖZETLE — sessiz başarı yok. */
  async function reverseShipment(reason: string) {
    if (!revTarget) return;
    try {
      const r = await reverseMut.mutateAsync({ id: revTarget.id, reason });
      await qc.invalidateQueries();
      setRevTarget(null);
      setRevResult(
        `${r.code} geri alındı ve silindi — ${r.returnedQty} birim depoya döndü` +
        (r.reopenedLots.length ? ` · ${r.reopenedLots.length} lot yeniden planlanabilir` : "") +
        (r.reopenedOrders.length ? ` · sipariş yeniden açıldı: ${r.reopenedOrders.join(", ")}` : ""),
      );
    } catch { /* hata reverseMut.error ile dialog içinde gösterilir; dialog açık kalır */ }
  }

  // "Sevkiyat Planla": bu sipariş için açık taslak varsa kullanıcıya SOR (mevcut taslağı aç / kalan için yeni
  // paralel sevkiyat). Açık taslak yoksa doğrudan planla.
  function planShipment(orderId: string) {
    const order = readyOrders.find((o) => o.id === orderId);
    if (order?.openDraftCode) { setPlanErr(null); setPlanNote(null); setPlanChoice({ orderId, draftCode: order.openDraftCode }); return; }
    void doPlan(orderId, false);
  }
  async function doPlan(orderId: string, forceNew: boolean) {
    setPlanErr(null); setPlanNote(null); setPlanChoice(null);
    try {
      const res = await createMut.mutateAsync({ orderId, forceNew });
      await qc.invalidateQueries();
      if (res.warnings && res.warnings.length > 0) setPlanNote(res.warnings.join(" · "));
      setDetailId(res.shipmentId); // plan/evrak çekmecesini aç (forceNew=false + açık taslak → o tazelenir; forceNew → yeni)
    } catch (e) { setPlanErr((e as Error).message); }
  }

  // Manuel sevkiyat: boş çok-siparişli taslak oluştur → detay çekmecesinde elle lot atanır.
  async function newManualShipment() {
    setPlanErr(null); setPlanNote(null);
    try {
      const res = await createManualMut.mutateAsync();
      await qc.invalidateQueries();
      setDetailId(res.shipmentId);
    } catch (e) { setPlanErr((e as Error).message); }
  }

  const READY_BADGE: Record<string, { cls: string; label: string }> = {
    ready: { cls: "b-success", label: "Sevke Hazır" },
    in_production: { cls: "b-warn", label: "Üretimde (kısmi)" },
    confirmed: { cls: "b-info", label: "Onaylı" },
  };

  const rows = (shipsQ.data?.rows ?? []) as unknown as ShipmentRow[];
  const total = shipsQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // readyPage'in eşleniği: liste küçülünce (sekme değişimi / silme / filtre) sayfa aralık dışı kalmasın.
  useEffect(() => { if (shipsQ.data && page > totalPages) setPage(totalPages); }, [shipsQ.data, totalPages, page]);

  // ═══ Aksiyon drawer/dialog'ları — masaüstü + mobil ORTAK (Drawer ≤767px'te tam-ekran) ═══
  const drawers = (
    <>
      <CreateShipmentDrawer open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id, warnings) => { setDetailId(id); setPlanNote(warnings && warnings.length > 0 ? warnings.join(" · ") : null); }} />
      <ShipmentDetailDrawer open={!!detailId} shipmentId={detailId} onClose={() => setDetailId(null)} onDeleted={(message) => pushToast({ tone: "success", message })} />
      {planChoice ? (
        <div role="dialog" aria-modal="true" aria-label="Açık taslak seçimi" onClick={() => setPlanChoice(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} className="d-card" style={{ maxWidth: 480, width: "100%", padding: 20 }}>
            <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "var(--neutral-900)" }}>Bu sipariş için açık taslak var</h3>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--neutral-600)", lineHeight: 1.5 }}>
              <b className="mono">{planChoice.draftCode}</b> taslağı hâlâ açık. Mevcut taslağı mı düzenlemek, yoksa <b>kalan mamüller</b> için AYRI/paralel yeni bir sevkiyat mı açmak istersiniz?
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button type="button" className="btn-md secondary" onClick={() => setPlanChoice(null)}>Vazgeç</button>
              <button type="button" className="btn-md secondary" disabled={createMut.isPending} onClick={() => doPlan(planChoice.orderId, false)}><Icon name="edit" size={14} />Mevcut Taslağı Aç</button>
              <button type="button" className="btn-md primary" disabled={createMut.isPending} onClick={() => doPlan(planChoice.orderId, true)}><Icon name="plus" size={14} />Kalan İçin Yeni Sevkiyat</button>
            </div>
          </div>
        </div>
      ) : null}
      <ShippableOrderDetailDrawer open={!!detailOrderId} orderId={detailOrderId} onClose={() => setDetailOrderId(null)} />
      <HardDeleteConfirm
        open={!!delTarget}
        code={delTarget?.code ?? ""}
        title="Sevkiyat planını sil"
        warning={
          <>
            {/* Onaysız (boş plan) silme sunucuda reddedildiyse NEDENİ: kullanıcı neden pencereye düştüğünü bilsin. */}
            {delTarget?.not ? <div className="form-banner-warn" role="status"><Icon name="alertTriangle" size={14} />{delTarget.not}</div> : null}
            <b>{delTarget?.code}</b> sevkiyat planı <b>kalıcı olarak</b> silinecek (satırlar ve yüklü evraklar dahil). Bu işlem geri alınamaz.
          </>
        }
        busy={removeMut.isPending}
        errorMessage={removeMut.error?.message ?? null}
        onConfirmed={removeShipment}
        onClose={() => setDelTarget(null)}
      />

      {/* ═══ SEVK EDİLMİŞ SEVKİYATI GERİ AL — iki adım: ne olacak → neden ═══
          Kod yazdırmak yerine GEREKÇE isteniyor: ikisi de "kasıtlı eylem" kanıtı, ama gerekçe
          denetim kaydına yazıldığı için sonradan "bu sevkiyat neden silinmiş?" sorusunu cevaplar. */}
      {revTarget && revStep === "warn" ? (
        <ConfirmDialog
          open
          title="Sevkiyatı geri al ve sil"
          tone="danger"
          confirmLabel="Devam Et"
          cancelLabel="Vazgeç"
          message={
            <>
              <p style={{ marginTop: 0 }}>
                <b className="mono">{revTarget.code}</b> sevk edilmiş bir sevkiyattır
                {revTarget.orderCode ? <> (<span className="mono">{revTarget.orderCode}</span>)</> : null}. Şunlar olacak:
              </p>
              <ul className="shp-rev-list">
                <li><b>Stok geri gelir</b> — {revTarget.lineCount} satırın malı çıktığı depoya iade edilir.</li>
                <li><b>Lotlar yeniden planlanabilir</b> hale gelir (yeni sevkiyata eklenebilir).</li>
                <li>Siparişin <b>sevk edilen miktarı düşer</b>; sipariş kapandıysa yeniden açılır.</li>
                <li><b>Sevkiyat kaydı silinir</b> — irsaliye bilgileri yalnız denetim kaydında kalır.</li>
              </ul>
              <p style={{ marginBottom: 0, fontSize: 12.5, color: "var(--neutral-600)" }}>
                Stok hareketleri silinmez; tersi yazılır, böylece geçmiş defterde iz kalır.
                Yüklenmiş evrak dosyaları diskte korunur.
              </p>
            </>
          }
          onConfirm={() => { reverseMut.reset(); setRevStep("reason"); }}
          onCancel={() => setRevTarget(null)}
        />
      ) : null}
      {revTarget && revStep === "reason" ? (
        <PromptDialog
          open
          title={`${revTarget.code} — geri alma gerekçesi`}
          tone="danger"
          icon="alertTriangle"
          label="Gerekçe"
          placeholder="Örn. Yanlış lotlar seçilmiş, doğru partilerle yeniden planlanacak"
          multiline
          maxLength={300}
          confirmLabel="Geri Al ve Sil"
          cancelLabel="Vazgeç"
          busy={reverseMut.isPending}
          message={reverseMut.error ? <div className="form-banner-err" role="alert" style={{ marginBottom: 10 }}>{reverseMut.error.message}</div> : undefined}
          validate={(v) => (v.trim().length >= 10 ? null : "Gerekçe en az 10 karakter olmalı (denetim kaydına yazılır).")}
          onConfirm={(v) => void reverseShipment(v.trim())}
          onCancel={() => setRevTarget(null)}
        />
      ) : null}

      {/* ═══ İDARİ KAPATMA — ne olacağı ÖNCEDEN sayılır ═══
          Bu buton stok defterini ve cari borcu tek tıkla yazıyor; "emin misiniz?" yetmez,
          kullanıcı hangi sonuçların doğacağını görmeli. Gerekçe denetim kaydına gider. */}
      {closeTarget ? (
        <PromptDialog
          open
          title={`${closeTarget.code} — sevkiyatı kapat`}
          /* PromptDialog yalnız "primary" | "danger" biliyor. Bu işlem yıkıcı değil ama
             geri dönüşü zahmetli (stok + cari) → primary, ağırlığı metin taşıyor. */
          tone="primary"
          icon="truck"
          label="Kapatma gerekçesi"
          placeholder="Örn. Mal fiilen sevk edildi, irsaliye evrakı sonradan sisteme girilecek"
          multiline
          maxLength={300}
          confirmLabel="Kapat ve Stok Düş"
          cancelLabel="Vazgeç"
          busy={adminCloseMut.isPending}
          message={
            <>
              <p style={{ marginTop: 0 }}>
                <b>{closeTarget.customer}</b> siparişi için sevkiyat oluşturulup <b>onaylanacak</b>:
              </p>
              <ul className="shp-rev-list">
                <li><b>Stok düşülür</b> — siparişe bağlı partilerden FEFO ile çıkış yazılır.</li>
                <li><b>Cari borç yazılır</b> — sevk bedeli müşterinin hesabına işlenir.</li>
                <li><b>Sevk tarihi</b> siparişin teslim tarihinden alınır.</li>
                <li>Sipariş tamamen karşılanırsa <b>“sevk edildi”</b> olur.</li>
              </ul>
              <p style={{ marginBottom: 0, fontSize: 12.5, color: "var(--neutral-600)" }}>
                Evrak alanları (plaka · sürücü · irsaliye no · kantar) <b>boş kalır</b>; sevkiyat
                “idari kapatma” olarak işaretlenir ve raporda böyle görünür. Stok yetmezse işlem
                yapılmaz, eksik kalemler size bildirilir.
              </p>
              {adminCloseMut.error ? <div className="form-banner-err" role="alert" style={{ marginTop: 10 }}>{adminCloseMut.error.message}</div> : null}
            </>
          }
          validate={(v) => (v.trim().length >= 10 ? null : "Gerekçe en az 10 karakter olmalı (denetim kaydına yazılır).")}
          onConfirm={(v) => void adminCloseOrder(v.trim())}
          onCancel={() => setCloseTarget(null)}
        />
      ) : null}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </>
  );

  // ═══ MOBİL GÖRÜNÜM (≤767px) — hazır siparişler + sevkiyat kartları ═══
  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn-md secondary sm" style={{ flex: 1 }} onClick={newManualShipment} disabled={createManualMut.isPending}><Icon name="edit" size={14} />{createManualMut.isPending ? "…" : "Manuel"}</button>
            <button type="button" className="btn-md primary sm" style={{ flex: 1 }} onClick={() => setCreateOpen(true)}><Icon name="plus" size={14} />Yeni Sevkiyat</button>
          </div>
          <PageTabs tabs={SHIP_TABS} active={tab} onChange={changeTab} mobile />
        </div>
        {planErr ? <div className="form-banner-err" role="alert">{planErr}</div> : null}
        {planNote ? <div className="form-banner-info" role="status">{planNote}</div> : null}
        {/* Geri alma özeti: modal DEĞİL sayfa şeridi — kullanıcı sonucu okurken listeyi de görsün. */}
        {revResult ? <div className="form-banner-info shp-rev-ok" role="status"><Icon name="undo" size={14} />{revResult}<button type="button" className="iact" aria-label="Kapat" onClick={() => setRevResult(null)}><Icon name="x" size={12} /></button></div> : null}
        {/* İdari kapatma özeti — hangi sevkiyat kodu doğdu, kaç satır, hangi tarih. */}
        {closeResult ? <div className="form-banner-info shp-rev-ok" role="status"><Icon name="checkCircle" size={14} />{closeResult}<button type="button" className="iact" aria-label="Kapat" onClick={() => setCloseResult(null)}><Icon name="x" size={12} /></button></div> : null}

        {tab === "ready" ? (
          <>
            <div className="m-sec">Sevkiyata Hazır ({readyTotal})</div>
            <div className="m-search" style={{ height: 44 }}>
              <Icon name="search" size={15} />
              <input type="search" placeholder="Sipariş / müşteri ara…" value={readySearch} onChange={(e) => { setReadySearch(e.target.value); setReadyPage(1); }} aria-label="Sevkiyata hazır sipariş ara" />
            </div>
            {readyOrders.length === 0 ? <div className="m-empty">{readySearch ? `"${readySearch}" için sonuç yok.` : "Sevkiyata hazır sipariş yok."}</div> : null}
            {readyOrders.map((o) => {
              const rb = READY_BADGE[o.status] ?? { cls: "b-neutral", label: o.status };
              return (
                <MCard pad key={o.id}>
                  <button type="button" onClick={() => setDetailOrderId(o.id)}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: 0, padding: 0, cursor: "pointer", textAlign: "left" }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="mono" style={{ display: "block", fontSize: 13, fontWeight: 700, color: "var(--info)" }}>{o.code}</span>
                      <span style={{ display: "block", fontSize: 12.5, color: "var(--neutral-500)", marginTop: 2 }}>{o.customerName}</span>
                    </span>
                    <span className={`badge ${rb.cls === "b-warn" ? "b-warning" : rb.cls} no-dot`}>{rb.label}</span>
                  </button>
                  <div className="so-lines" style={{ marginTop: 8 }}>
                    {o.lines.map((ln, i) => (
                      <div className="so-line" key={i}>
                        <span className="so-line-name" title={`${ln.itemName} (${ln.itemCode})`}>{ln.itemName}</span>
                        <span className="so-line-qty mono">
                          <span className="so-prod">{fmtQty(ln.produced)}</span>
                          <span className="so-slash">/</span>
                          <span className={ln.remaining > 0 ? "so-rem-open" : "so-rem-done"}>{fmtQty(ln.remaining)}</span>
                          <span className="so-uom">{ln.uom}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 8 }}>
                    <KV k="Sipariş T." v={fmtDate(o.createdAt)} mono />
                    <KV k="Sevk T. (vaat)" v={fmtDate(o.promisedDate)} mono />
                  </div>
                  <button type="button" className="btn-md primary" style={{ width: "100%", marginTop: 10 }} onClick={() => planShipment(o.id)} disabled={createMut.isPending}>
                    <Icon name="truck" size={15} />{createMut.isPending ? "…" : "Sevkiyat Planla"}
                  </button>
                </MCard>
              );
            })}
            <MobilePager page={readyPage} totalPages={readyTotalPages} onPage={setReadyPage} />
          </>
        ) : (
        <>
        <div className="m-sec">{tab === "done" ? `Tamamlanan (${total})` : "Sevkiyatlar"}</div>
        <div className="m-search" style={{ height: 44 }}>
          <Icon name="search" size={15} />
          <input type="search" placeholder="Sevkiyat / sipariş kodu ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
        </div>
        {tab === "shipments" ? (
          <div style={{ display: "flex" }}>
            <button type="button" className={`m-chip ${showCancelled ? "act" : ""}`} onClick={() => { setShowCancelled(!showCancelled); setPage(1); }}>İptalleri göster</button>
          </div>
        ) : null}

        {shipsQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">{bosMetin ?? (tab === "done" ? "Tamamlanan sevkiyat yok." : "Hazırlanan sevkiyat yok — tamamlananlar “Tamamlanan” sekmesinde.")}</div>
        ) : (
          rows.map((s) => {
            const sb = STATUS_BADGE[s.status];
            return (
              <MCard pad key={s.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="mono" style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: "var(--neutral-900)" }}>{s.code}</span>
                  <span className={`badge ${sb.cls === "b-warn" ? "b-warning" : sb.cls || "b-neutral"} no-dot`}>{sb.label}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 8 }}>
                  <KV k="Sipariş" v={s.orderCode} mono />
                  <KV k="Müşteri" v={s.customerName} />
                  <KV k="Satır" v={s.lineCount} mono />
                  <KV k="Sevk Tarihi" v={fmtDate(s.shippedAt)} mono />
                  <KV k="Nakliye" v={
                    s.freightParty === "firma" && s.freightAmount != null
                      ? `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2 }).format(s.freightAmount)} ${s.freightCurrency ?? ""}`
                      : s.freightParty === "karsi" ? "Karşıya ait" : "—"
                  } mono={s.freightParty === "firma"} />
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Detay" onClick={() => setDetailId(s.id)}><Icon name="eye" size={16} /></button>
                  {s.status !== "confirmed" ? (
                    // Masaüstüyle AYNI davranış: boş plan onaysız (silTiklandi), dolu plan kod onayıyla.
                    <button type="button" className="m-iconbtn m-li-act danger"
                      aria-label={s.bosPlan ? "Boş planı sil (onay sorulmaz)" : "Sevkiyat planını sil"}
                      disabled={removeMut.isPending || bosSiliniyor} onClick={() => void silTiklandi(s)}><Icon name="trash" size={16} /></button>
                  ) : isSuper ? (
                    // Masaüstüyle AYNI yetki — mobilde eksik bırakmak "telefondan düzeltemiyorum" demek olurdu.
                    <button type="button" className="m-iconbtn m-li-act danger" aria-label="Sevkiyatı geri al ve sil"
                      onClick={() => { reverseMut.reset(); setRevStep("warn"); setRevTarget({ id: s.id, code: s.code, orderCode: s.orderCode ?? null, lineCount: s.lineCount }); }}><Icon name="undo" size={16} /></button>
                  ) : null}
                </div>
              </MCard>
            );
          })
        )}
        <MobilePager page={page} totalPages={totalPages} onPage={setPage} />
        </>
        )}

        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Sevkiyat</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Sevkiyat</h1>
          <p className="sub">Pick list · stok çıkışı · irsaliye — sipariş döngüsünü kapatır</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="btn-md secondary" onClick={newManualShipment} disabled={createManualMut.isPending}><Icon name="edit" size={14} />{createManualMut.isPending ? "…" : "Manuel Sevkiyat"}</button>
          <button type="button" className="btn-md primary" onClick={() => setCreateOpen(true)}><Icon name="plus" size={14} />Yeni Sevkiyat</button>
        </div>
      </div>

      {planErr ? <div className="form-banner-err" role="alert" style={{ marginBottom: 12 }}>{planErr}</div> : null}
      {planNote ? <div className="form-banner-info" role="status" style={{ marginBottom: 12 }}>{planNote}</div> : null}
      {revResult ? <div className="form-banner-info shp-rev-ok" role="status"><Icon name="undo" size={14} />{revResult}<button type="button" className="iact" aria-label="Kapat" onClick={() => setRevResult(null)}><Icon name="x" size={12} /></button></div> : null}

      <PageTabs tabs={SHIP_TABS} active={tab} onChange={changeTab} />

      {tab === "ready" ? (
        <div className="d-card" style={{ marginBottom: 16 }}>
          <div className="d-card-head">
            <div className="left"><span className="t">Sevkiyata Hazır Siparişler</span><span className="s">{readyTotal} sipariş · üretim + kalite onaylı</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div className="dt2-search-box">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
                <input type="search" placeholder="Sipariş / müşteri ara…" value={readySearch} onChange={(e) => { setReadySearch(e.target.value); setReadyPage(1); }} aria-label="Sevkiyata hazır sipariş ara" />
              </div>
              <button type="button" className="btn" onClick={() => readyQ.refetch()} disabled={readyQ.isFetching}>{readyQ.isFetching ? "…" : "Yenile"}</button>
            </div>
          </div>
          <div className="d-card-body flush">
            <table className="dt2">
              <thead><tr>
                <th style={{ width: 132 }}>Sipariş</th>
                <th style={{ width: 150 }}>Müşteri</th>
                <th>Kalemler · <span className="so-h-prod">Üretilen</span> / <span className="so-h-rem">Kalan</span></th>
                <th style={{ width: 94 }}>Sipariş T.</th>
                <th style={{ width: 110 }}>Sevk T. (vaat)</th>
                <th style={{ width: 124 }}>Durum</th>
                <th style={{ width: 150 }}>İşlem</th>
              </tr></thead>
              <tbody>
                {readyOrders.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: "center", padding: 18, color: "var(--neutral-500)" }}>{readySearch ? `"${readySearch}" için sonuç yok.` : "Sevkiyata hazır sipariş yok."}</td></tr>
                ) : null}
                {readyOrders.map((o) => {
                  const rb = READY_BADGE[o.status] ?? { cls: "", label: o.status };
                  return (
                    <tr key={o.id} onClick={() => setDetailOrderId(o.id)} style={{ cursor: "pointer" }} title="Sipariş detayını gör">
                      <td><span className="cell-id" style={{ color: "var(--info)" }}>{o.code}</span></td>
                      <td>{o.customerName}</td>
                      <td>
                        <div className="so-lines">
                          {o.lines.map((ln, i) => (
                            <div className="so-line" key={i}>
                              <span className="so-line-name" title={`${ln.itemName} (${ln.itemCode})`}>{ln.itemName}</span>
                              <span className="so-line-qty mono" title={`Üretilen ${fmtQty(ln.produced)} / Kalan ${fmtQty(ln.remaining)} ${ln.uom} · Sipariş ${fmtQty(ln.ordered)} ${ln.uom}`}>
                                <span className="so-prod">{fmtQty(ln.produced)}</span>
                                <span className="so-slash">/</span>
                                <span className={ln.remaining > 0 ? "so-rem-open" : "so-rem-done"}>{fmtQty(ln.remaining)}</span>
                                <span className="so-uom">{ln.uom}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td><span className="mono so-date">{fmtDate(o.createdAt)}</span></td>
                      <td><span className="mono so-date">{fmtDate(o.promisedDate)}</span></td>
                      <td>
                        <span className={`badge-pill ${rb.cls}`}>{rb.label}</span>
                        {/* SEVKİYAT GEÇMİŞİ ROZETİ — canlıda SO-2026-00011 için 7 plan yapılıp
                            7'si de iptal edilmişti ve kullanıcı "sipariş sevk edildi" sanıyordu;
                            ekranda o planların hiçbir izi yoktu. Rozet yanlış anlamayı keser. */}
                        {o.cancelledShipments > 0 ? (
                          <span className="badge-pill b-danger" style={{ marginLeft: 4 }}
                            title={`Bu sipariş için ${o.cancelledShipments} sevkiyat planı açılıp İPTAL edilmiş — hiçbiri sevk edilmedi.`}>
                            {o.cancelledShipments} iptal
                          </span>
                        ) : null}
                        {o.confirmedShipments > 0 ? (
                          <span className="badge-pill b-success" style={{ marginLeft: 4 }}
                            title={`${o.confirmedShipments} sevkiyat onaylandı (kısmi sevk).`}>
                            {o.confirmedShipments} sevk
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button type="button" className="btn-md primary" style={{ height: 30, fontSize: 12.5 }} onClick={(e) => { e.stopPropagation(); planShipment(o.id); }} disabled={createMut.isPending}>
                            <Icon name="truck" size={14} />{createMut.isPending ? "…" : "Sevkiyat Planla"}
                          </button>
                          {/* İdari kapatma YALNIZ süper-adminde görünür: stok defterini ve cari borcu
                              tek tıkla yazan bir işlem, evrak adımlarını atlayarak. */}
                          {isSuper ? (
                            <button type="button" className="btn-md secondary" style={{ height: 30, fontSize: 12.5 }}
                              title="Evrak girmeden kapat: stok düşülür, cari borç yazılır, sevk tarihi siparişten alınır"
                              onClick={(e) => { e.stopPropagation(); adminCloseMut.reset(); setCloseTarget({ orderId: o.id, code: o.code, customer: o.customerName }); }}
                              disabled={adminCloseMut.isPending}>
                              <Icon name="checkCircle" size={14} />Sevkiyatı Kapat
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <TablePager total={readyTotal} unit="sipariş" page={readyPage} totalPages={readyTotalPages} onPage={setReadyPage} />
        </div>
      ) : (
      <div className="dt2-wrap">
        <div className="dt2-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input type="search" placeholder="Plaka · ürün · müşteri · sevkiyat/sipariş kodu ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
          {/* Çok seçimli süzgeçler — sunucu tarafında AND'lenir, arama kutusunu EZMEZ. */}
          <div style={{ minWidth: 190 }}>
            <SearchSelect
              value={null}
              multiValues={customerIds}
              onChangeMulti={(v) => { setCustomerIds(v); setPage(1); }}
              multiSummary={(n) => `${n} müşteri`}
              onChange={() => {}}
              options={musteriSecenek}
              placeholder="Müşteri"
              searchPlaceholder="Müşteri ara…"
              emptyText="Müşteri bulunamadı"
            />
          </div>
          <div style={{ minWidth: 190 }}>
            <SearchSelect
              value={null}
              multiValues={itemIds}
              onChangeMulti={(v) => { setItemIds(v); setPage(1); }}
              multiSummary={(n) => `${n} ürün`}
              onChange={() => {}}
              options={urunSecenek}
              placeholder="Ürün"
              searchPlaceholder="Ürün adı / kodu ara…"
              emptyText="Ürün bulunamadı"
            />
          </div>
          {customerIds.length || itemIds.length ? (
            <button type="button" className="btn" onClick={() => { setCustomerIds([]); setItemIds([]); setPage(1); }}>Süzgeci temizle</button>
          ) : null}
          <span className="grow" />
          {tab === "shipments" ? (
            <ToggleSwitch checked={showCancelled} onChange={(v) => { setShowCancelled(v); setPage(1); }} label="İptal edilenleri göster" />
          ) : null}
          <button type="button" className="btn" onClick={() => shipsQ.refetch()} disabled={shipsQ.isFetching}>{shipsQ.isFetching ? "Yükleniyor…" : "Yenile"}</button>
        </div>

        <div className="dt2-scroll">
          {shipsQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">{tab === "done" ? "Tamamlanan sevkiyat yok" : "Hazırlanan sevkiyat yok"}</p>
              <p className="sub">{bosMetin ?? (tab === "done" ? "Onaylanan (stok çıkışı yapılan) sevkiyatlar burada listelenir." : "Bu listede hazırlanan (taslak) sevkiyatlar yer alır — tamamlananlar “Tamamlanan” sekmesindedir.")}</p>
              {!q && !suzgecAktif && tab !== "done" ? <button type="button" className="btn-md primary" style={{ marginTop: 12 }} onClick={() => setCreateOpen(true)}><Icon name="plus" size={14} />Yeni Sevkiyat</button> : null}
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Sevkiyat</th>
                  <th style={{ width: 130 }}>Sipariş</th>
                  <th style={{ width: 150 }}>Müşteri</th>
                  <th style={{ width: 108 }}>Plaka</th>
                  <th>Ürün · <span style={{ fontWeight: 600, textTransform: "none" }}>sevk / sipariş</span></th>
                  <th style={{ width: 112 }}>Plan</th>
                  <th style={{ width: 104 }}>Sevk Tarihi</th>
                  <th className="num" style={{ width: 120 }}>Nakliye Gideri</th>
                  <th style={{ width: 120 }}>Durum</th>
                  <th style={{ width: 64 }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const sb = STATUS_BADGE[s.status];
                  return (
                    <tr key={s.id}>
                      <td><span className="cell-id">{s.code}</span></td>
                      <td><span className="mono" style={{ fontSize: 12 }}>{s.orderCode}</span></td>
                      <td>{s.customerName}</td>
                      <td>
                        {s.vehiclePlate ? (
                          <span className="mono" style={{ fontSize: 12 }}>
                            {s.vehiclePlate}
                            {s.trailerPlate ? <span style={{ display: "block", fontSize: 10.5, color: "var(--neutral-500)" }}>{s.trailerPlate}</span> : null}
                          </span>
                        ) : <span style={{ color: "var(--neutral-400)" }}>—</span>}
                      </td>
                      <td>
                        {/* Ürün özeti: sevk edilecek / siparişe esas miktar. İki rakam birlikte
                            durur ki "bu sevkiyat siparişin ne kadarını götürüyor" tek bakışta okunsun. */}
                        {s.kalemler.length === 0 ? (
                          <span style={{ color: "var(--neutral-400)" }}>—</span>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {s.kalemler.slice(0, 3).map((k) => (
                              <span key={`${k.code}-${k.uom}`} style={{ fontSize: 12 }}>
                                {k.name}
                                <span className="mono" style={{ marginLeft: 6, color: "var(--neutral-700)" }}>
                                  {fmtQty(k.sevk)} / {fmtQty(k.siparis)} {k.uom}
                                </span>
                              </span>
                            ))}
                            {s.kalemler.length > 3 ? (
                              <span style={{ fontSize: 11, color: "var(--neutral-500)" }}>+{s.kalemler.length - 3} ürün daha · {s.lineCount} satır</span>
                            ) : (
                              <span style={{ fontSize: 11, color: "var(--neutral-500)" }}>{s.lineCount} satır</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {/* Planlanan sevk tarihi + planın YAPILDIĞI tarih (kayıt oluşturma). */}
                        <span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-700)" }}>{fmtDate(s.plannedDate)}</span>
                        <span className="mono" style={{ display: "block", fontSize: 10.5, color: "var(--neutral-500)" }} title="Sevk planının yapıldığı tarih">
                          plan: {fmtDate(s.createdAt)}
                        </span>
                      </td>
                      <td><span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{fmtDate(s.shippedAt)}</span></td>
                      <td className="cell-num">
                        {s.freightParty === "firma" && s.freightAmount != null
                          ? <span className="mono" style={{ fontSize: 12 }}>{new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2 }).format(s.freightAmount)} {s.freightCurrency ?? ""}</span>
                          : s.freightParty === "karsi"
                            ? <span style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>Karşıya ait</span>
                            : <span style={{ color: "var(--neutral-400)" }}>—</span>}
                      </td>
                      <td><span className={`badge-pill ${sb.cls}`}>{sb.label}</span></td>
                      <td>
                        <div className="row-acts">
                          <button type="button" className="iact info" aria-label="Detay" title="Detay" onClick={() => setDetailId(s.id)}>
                            <Icon name="eye" size={15} />
                          </button>
                          {s.status !== "confirmed" ? (
                            /* Boş plan: onay sorulmadan silinir (title bunu SÖYLER); dolu plan: kod onayı. */
                            <button type="button" className="iact danger"
                              aria-label={s.bosPlan ? "Boş planı sil" : "Sil"}
                              title={s.bosPlan ? "Boş plan — onay sorulmadan silinir" : "Sevkiyat planını kalıcı sil"}
                              disabled={removeMut.isPending || bosSiliniyor} onClick={() => void silTiklandi(s)}>
                              <Icon name="trash" size={15} />
                            </button>
                          ) : isSuper ? (
                            /* SEVK EDİLMİŞ sevkiyat: silinmez, GERİ ALINIR. İkon "undo" — eylem
                               düz silme değil, stok defterini geri sarma. Yalnız süper adminde görünür. */
                            <button type="button" className="iact danger" aria-label="Sevkiyatı geri al ve sil"
                              title="Sevkiyatı geri al: stok depoya döner, lotlar yeniden planlanabilir"
                              onClick={() => { reverseMut.reset(); setRevStep("warn"); setRevTarget({ id: s.id, code: s.code, orderCode: s.orderCode ?? null, lineCount: s.lineCount }); }}>
                              <Icon name="undo" size={15} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <TablePager total={total} unit="sevkiyat" page={page} totalPages={totalPages} onPage={setPage} />
      </div>
      )}

      {drawers}
    </div>
  );
}
